// MapView.jsx - Modified for vendors with location pin icon
import { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const VENDOR_PIN_COLOR = '#1e88e5';

const createLocationPinIcon = () => {
  return L.divIcon({
    html: `<svg width="30" height="32" viewBox="0 0 24 35" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 0C5.37 0 0 5.37 0 12c0 9 12 23 12 23s12-14 12-23c0-6.63-5.37-12-12-12z" fill="#E53935" stroke="white" stroke-width="2"/>
      <circle cx="12" cy="12" r="4" fill="white"/>
    </svg>`,
    iconSize: [32, 42],
    popupAnchor: [0, -20],
    className: 'location-pin-marker',
    bgPos: [0, 0],
    popupAnchor: [0, -20],
    tooltipAnchor: [0, -20]
  });
};
const createVendorPinIcon = (isSelected = false) => {
  const selectedStroke = isSelected ? '' : 'white';
  const selectedStrokeWidth = isSelected ? '3' : '2';
  
  return L.divIcon({
    html: `<svg width="${isSelected ? '30' : '30'}" height="${isSelected ? '30' : '30'}" viewBox="0 0 24 35" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 0C5.37 0 0 5.37 0 12c0 9 12 23 12 23s12-14 12-23c0-6.63-5.37-12-12-12z" fill="${VENDOR_PIN_COLOR}" stroke="${selectedStroke}" stroke-width="${selectedStrokeWidth}"/>
      <circle cx="12" cy="12" r="4" fill="white"/>
      ${isSelected ? '<circle cx="12" cy="12" r="2" "/>' : ''}
    </svg>`,
    iconSize: [isSelected ? 36 : 32, isSelected ? 46 : 42],
    popupAnchor: [0, isSelected ? -23 : -20],
    className: 'vendor-pin-marker',
    bgPos: [0, 0]
  });
};

const MapView = ({
  onViewportChange,
  onPropertiesUpdate,
  filters,
  selectedId,
  onMarkerClick,
  flyTarget,
  searchLocation,
  vendors = [],
}) => {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const clusterGroupRef = useRef(null);
  const markerByIdRef = useRef(new Map());
  const searchMarkerRef = useRef(null);
  const [mapReady, setMapReady] = useState(false);

  // Initialize map
  useEffect(() => {
    if (mapRef.current || !containerRef.current) return;

    const map = L.map(containerRef.current, {
      center: [20.5937, 78.9629],
      zoom: 5,
      minZoom: 4,
      maxZoom: 18,
      zoomControl: false,
    });
    
    mapRef.current = map;

    const style = document.createElement('style');
    style.textContent = `
      .leaflet-control-zoom {
        margin-top: 110px !important;
      }
    `;
    document.head.appendChild(style);

    L.control.zoom({ position: 'topright' }).addTo(map);
 
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap contributors',
      maxZoom: 19,
      minZoom: 4
    }).addTo(map);

    const clusterGroup = L.markerClusterGroup({
      chunkedLoading: true,
      disableClusteringAtZoom: 14,
      maxClusterRadius: 50,
    });
    clusterGroupRef.current = clusterGroup;
    map.addLayer(clusterGroup);

    map.whenReady(() => {
      setMapReady(true);
    });

    const handleMoveEnd = () => {
      if (!mapRef.current) return;
      const bounds = mapRef.current.getBounds();
      onViewportChange?.({
        bounds: {
          south: bounds.getSouth(),
          north: bounds.getNorth(),
          west: bounds.getWest(),
          east: bounds.getEast()
        },
        zoom: mapRef.current.getZoom(),
      });
    };

    map.on('moveend', handleMoveEnd);

    return () => {
      if (style.parentNode) style.parentNode.removeChild(style);
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, [onViewportChange]);

  // Update markers when vendors change
  useEffect(() => {
    if (!mapReady || !clusterGroupRef.current) return;

    const cluster = clusterGroupRef.current;
    cluster.clearLayers();
    markerByIdRef.current.clear();

    vendors.forEach(vendor => {
      const lat = parseFloat(vendor.lat);
      const lng = parseFloat(vendor.long);

      if (isNaN(lat) || isNaN(lng)) return;

      const isSelected = selectedId === vendor.vendor_id;
      const icon = createVendorPinIcon(isSelected); 
      const marker = L.marker([lat, lng], { icon });
      marker.on('click', () => onMarkerClick(vendor));
      marker.bindPopup(`
        <b>${vendor.name}</b><br/>
        ${vendor.profession}<br/>
       
      `);
      markerByIdRef.current.set(vendor.vendor_id, marker);
      cluster.addLayer(marker);
    });
  }, [vendors, selectedId, mapReady, onMarkerClick]);

  // Handle search location pin
  useEffect(() => {
    if (!mapReady || !mapRef.current) return;

    if (searchMarkerRef.current && mapRef.current.hasLayer(searchMarkerRef.current)) {
      mapRef.current.removeLayer(searchMarkerRef.current);
      searchMarkerRef.current = null;
    }

    if (searchLocation && searchLocation.lat && searchLocation.lng) {
      const locationPin = createLocationPinIcon();
      searchMarkerRef.current = L.marker(
        [searchLocation.lat, searchLocation.lng], 
        { icon: locationPin }
      ).addTo(mapRef.current);
      
      if (searchLocation.name) {
        searchMarkerRef.current.bindPopup(`
          <b>📍 ${searchLocation.name}</b><br/>
          Searched Location
        `).openPopup();
      }
    }
  }, [searchLocation, mapReady]);

  // Fly to target
  useEffect(() => {
    if (!mapReady || !flyTarget || !mapRef.current) return;
    mapRef.current.flyTo([flyTarget.lat, flyTarget.lng], flyTarget.zoom || 14, { duration: 1.2 });
  }, [flyTarget, mapReady]);

  return <div ref={containerRef} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 0 }} />;
};

export default MapView;